import React, { useState, useMemo } from 'react';
import { useConfig } from '../context/ConfigContext';
import { useClients } from '../context/ClientsContext';
import { useUsers } from '../context/UsersContext';
import { C, S } from '../utils/styles';
import Icon from './Icon';
import AnalysisControls from './AnalysisControls';
import { fmt } from '../utils/formatters';
import { optimizeEmployee } from '../utils/calculations';
import TierCard from './TierCard';
import StyledSelect from './StyledSelect';
import CalculationAudit from './CalculationAudit';

function unitLabel(unit, qty) {
  const map = { day: 'days', visit: 'visits', trip: 'trips', month: 'months' };
  if (qty === 1) return unit;
  return map[unit] || unit + 's';
}

function fmtBenefitValue(ben) {
  return `${fmt(ben.fee)}/${ben.unit} × ${ben.quantity} ${unitLabel(ben.unit, ben.quantity)}`;
}

export default function IndividualView({ salary, setSalary, state, setState, filing, setFiling }) {
  const { config } = useConfig();
  const { activeClient } = useClients();
  const { currentUser } = useUsers();
  const [selectedId, setSelectedId] = useState(null);
  
  // Per-employee overrides
  const [employeePayFrequency, setEmployeePayFrequency] = useState(null);
  const [annualHours, setAnnualHours] = useState(2080);
  
  // Initialize annualHours from config once loaded
  React.useEffect(() => {
    if (config?.engineSettings) {
      setAnnualHours(config.engineSettings.annualHoursForMinWage || 2080);
    }
  }, []); // Only run once on mount
  
  // Create temporary config with analysis control overrides from activeClient
  const analysisConfig = useMemo(() => {
    const controls = activeClient?.analysisConfig?.controls || {};
    
    // Get values from activeClient.analysisConfig or fall back to system defaults
    const payFrequency = controls.payFrequency || config?.engineSettings?.payPeriodsPerYear || 26;
    const optimizationStrategy = controls.optimizationStrategy || config?.engineSettings?.optimizationStrategy || 'employee_benefit';
    const minEmployeeNetBenefit = controls.minEmployeeNet || config?.engineSettings?.minEmployeeNetBenefit || 0;
    const includeFuta = controls.includeFuta !== undefined ? controls.includeFuta : (config?.engineSettings?.includeFutaSavings || false);
    
    return {
      ...config,
      engineSettings: {
        ...config.engineSettings,
        payPeriodsPerYear: payFrequency,
        optimizationStrategy,
        minEmployeeNetBenefit,
        includeFutaSavings: includeFuta,
        annualHoursForMinWage: annualHours,
      }
    };
  }, [config, activeClient, annualHours]);
  
  const { results, optimal, hasViable, breakEvenSalary } = useMemo(
    () => optimizeEmployee(salary, state, filing, analysisConfig, employeePayFrequency),
    [salary, state, filing, analysisConfig, employeePayFrequency]
  );
  
  const sel = selectedId ? results.find((r) => r.id === selectedId) || optimal : optimal;
  const benefits = sel.benefits || [];
  
  // Get effective pay periods (employee override or analysis control from activeClient)
  const payFrequency = activeClient?.analysisConfig?.controls?.payFrequency || config?.engineSettings?.payPeriodsPerYear || 26;
  const optimizationStrategy = activeClient?.analysisConfig?.controls?.optimizationStrategy || config?.engineSettings?.optimizationStrategy || 'employee_benefit';
  const effectivePayPeriods = employeePayFrequency || payFrequency;
  const frequencyLabel = {
    12: 'Monthly',
    24: 'Semi-Monthly',
    26: 'Biweekly',
    52: 'Weekly',
  }[effectivePayPeriods] || 'Biweekly';
  
  const strategyLabel = {
    employee_benefit: 'Employee Benefit',
    employer_savings: 'Employer FICA',
    balanced: 'Balanced',
  }[optimizationStrategy];

  return (
    <div data-testid="individual-view" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Analysis Controls Bar */}
      <AnalysisControls />
      
      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 16, alignItems: "start" }}>
      <div style={S.card}>
        <h3 style={S.heading}>Employee Profile</h3>
        <div style={{ marginBottom: 12 }}>
          <label style={S.label}>Annual Salary</label>
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.accent, fontSize: 15, fontWeight: 700, ...S.mono }}>$</span>
            <input data-testid="salary-input" type="number" value={salary} onChange={(e) => setSalary(+e.target.value)} style={{ ...S.input, paddingLeft: 28 }} />
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={S.label}>State</label>
          <StyledSelect 
            data-testid="state-select" 
            value={state} 
            onValueChange={setState}
            options={Object.entries(config.stateData).sort((a, b) => a[1].name.localeCompare(b[1].name)).map(([code, stateInfo]) => ({
              value: code,
              label: stateInfo.name
            }))}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={S.label}>Filing Status</label>
          <StyledSelect 
            data-testid="filing-select" 
            value={filing} 
            onValueChange={setFiling}
            options={[
              { value: "single", label: "Single (S)" },
              { value: "married", label: "Married Filing Jointly (MFJ)" },
              { value: "mfs", label: "Married Filing Separately (MFS)" },
              { value: "hoh", label: "Head of Household (HoH)" },
              { value: "qss", label: "Qualifying Surviving Spouse (QSS)" }
            ]}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={S.label}>Pay Frequency</label>
          <StyledSelect 
            value={employeePayFrequency ? String(employeePayFrequency) : undefined} 
            onValueChange={(val) => setEmployeePayFrequency(val ? +val : null)}
            placeholder={`Use Analysis (${frequencyLabel})`}
            options={[
              { value: "12", label: "Monthly" },
              { value: "24", label: "Semi-Monthly" },
              { value: "26", label: "Biweekly" },
              { value: "52", label: "Weekly" }
            ]}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={S.label}>Annual Hours</label>
          <input
            type="number"
            value={annualHours}
            onChange={(e) => setAnnualHours(+e.target.value || 2080)}
            placeholder="2080"
            style={{ ...S.input, fontSize: 12 }}
          />
        </div>
        {config.stateData[state]?.conformityIncomeTax === false && (
          <div style={{ padding: "8px 10px", background: C.orangeLight, borderRadius: 8, border: "1px solid #fcd34d", fontSize: 11, color: C.orange, lineHeight: 1.4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="alert-circle" style={{ fontSize: '16px', flexShrink: 0 }} />
            {config.stateData[state].note}
          </div>
        )}

        {/* Revenue Model */}
        <div style={{ marginTop: 14, padding: "12px", background: C.purpleLight, borderRadius: 10, border: "1px solid #ddd6fe" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.purple, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>Revenue Model — {sel.label}</div>
          <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.7 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}><span>Premium</span><span style={{ fontWeight: 700, color: C.text, ...S.mono }}>{fmt(sel.monthlyPremium)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between" }}><span>− Claim</span><span style={{ fontWeight: 700, color: C.text, ...S.mono }}>{fmt(sel.monthlyClaim)}</span></div>
            <div style={{ borderTop: `1px solid #c4b5fd`, marginTop: 4, paddingTop: 4, display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700, color: C.purple }}>= HC Spread</span>
              <span style={{ fontWeight: 800, color: C.purple, ...S.mono }}>{fmt(sel.spread)}/mo</span>
            </div>
            <div style={{ fontSize: 10, color: C.textLight, marginTop: 6, lineHeight: 1.4 }}>
              Covers: vendor costs, Rx program, hospitalization underwriting, broker commissions, HC margin
            </div>
            <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", fontSize: 11 }}>
              <span>Annual spread</span>
              <span style={{ fontWeight: 800, color: C.purple, ...S.mono }}>{fmt(sel.hcAnnualRevenue)}</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* 6 Tier Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8 }}>
          {results.map((t) => (
            <TierCard key={t.id} tier={t} isOptimal={t.id === optimal.id} isSelected={t.id === sel.id} onClick={() => setSelectedId(t.id)} />
          ))}
        </div>

        {/* Eligibility Banner */}
        {!hasViable && (
          <div style={{ padding: "14px 18px", background: C.orangeLight, borderRadius: 10, border: "1px solid #fcd34d", display: "flex", alignItems: "center", gap: 12 }}>
            <Icon name="warning" style={{ fontSize: '32px', color: C.orange, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.orange, marginBottom: 2 }}>Not Recommended — No plan produces a net-positive outcome</div>
              <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.5 }}>
                At {fmt(salary)} in {config.stateData[state]?.name}, the tax savings do not fully offset the premium-to-claim spread on any tier.
                This employee would need a minimum salary of approximately <strong style={{ color: C.text, ...S.mono }}>{fmt(breakEvenSalary)}</strong> to benefit from Plan 600.
                The data below is shown for reference only — this employee should not be enrolled.
              </div>
            </div>
          </div>
        )}

        {hasViable && (
          <div style={{ padding: "10px 16px", background: C.greenLight, borderRadius: 10, border: `1px solid ${C.greenBorder}`, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>{"✅"}</span>
            <span style={{ fontSize: 13, color: C.green, fontWeight: 600 }}>
              Eligible — {results.filter(r => r.isViable).length} of {results.length} plans produce a net-positive outcome. {optimal.label} is optimal (+{fmt(optimal.employeePerPeriodBenefit)}/mo).
            </span>
          </div>
        )}

        {/* Summary Bar */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
          {[
            { label: "Tax Savings", value: fmt(sel.calc.diff.totalTax), sub: "/month", bg: C.greenLight, color: C.green },
            { label: "Net Premium Cost", value: fmt(sel.spread), sub: "/month (to HC)", bg: C.purpleLight, color: C.purple },
            { label: "Employee Impact", value: (sel.employeePerPeriodBenefit >= 0 ? "+" : "") + fmt(sel.employeePerPeriodBenefit), sub: "/month take-home", bg: sel.employeePerPeriodBenefit >= 0 ? C.greenLight : C.redLight, color: sel.employeePerPeriodBenefit >= 0 ? C.green : C.red },
            { label: "Annual Employee", value: (sel.employeeAnnualBenefit >= 0 ? "+" : "") + fmt(sel.employeeAnnualBenefit), sub: "/year", bg: sel.employeeAnnualBenefit >= 0 ? C.greenLight : C.redLight, color: sel.employeeAnnualBenefit >= 0 ? C.green : C.red },
            { label: "Employer FICA Saved", value: fmt(sel.employerAnnualFica), sub: "/year (zero cost)", bg: C.blueLight, color: C.blue },
          ].map((c, i) => (
            <div key={i} style={{ ...S.card, padding: "12px 10px", textAlign: "center", background: c.bg, border: "none" }}>
              <div style={{ fontSize: 9, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{c.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: c.color, ...S.mono }}>{c.value}</div>
              <div style={{ fontSize: 9, color: C.textLight }}>{c.sub}</div>
            </div>
          ))}
        </div>

        {/* Paycheck Detail */}
        <div style={S.card}>
          <h3 style={S.heading}>{frequencyLabel} Paycheck Breakdown — {sel.label}</h3>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={S.th}></th>
                <th style={{ ...S.th, textAlign: "right" }}>Without HealthCues</th>
                <th style={{ ...S.th, textAlign: "right" }}>With HealthCues</th>
                <th style={{ ...S.th, textAlign: "right" }}>Difference</th>
              </tr>
            </thead>
            <tbody>
              {[
                { l: "Gross Income", w: sel.calc.without.gross, h: sel.calc.with125.gross, d: 0, bold: true },
                { l: "HealthCues Pre-tax Premium", w: 0, h: sel.calc.with125.premium, d: sel.calc.with125.premium, hl: true },
                { l: "Taxable Income", w: sel.calc.without.gross, h: sel.calc.with125.taxableIncome, d: sel.calc.without.gross - sel.calc.with125.taxableIncome, bold: true },
                { l: "Tax Withholding", w: sel.calc.without.totalTax, h: sel.calc.with125.totalTax, d: sel.calc.diff.totalTax, bold: true },
                { l: "HealthCues Claim Payment", w: 0, h: sel.calc.with125.claim, d: sel.calc.with125.claim, hl: true },
                { l: "Net Take Home Pay", w: sel.calc.without.netTakeHome, h: sel.calc.with125.netTakeHome, d: sel.calc.diff.netTakeHome, bold: true, result: true },
                { divider: "Tax Detail" },
                { l: "Federal Tax", w: sel.calc.without.federalTax, h: sel.calc.with125.federalTax, d: sel.calc.diff.federalTax },
                { l: "Social Security", w: sel.calc.without.socialSecurity, h: sel.calc.with125.socialSecurity, d: sel.calc.diff.socialSecurity },
                { l: "Medicare", w: sel.calc.without.medicare, h: sel.calc.with125.medicare, d: sel.calc.diff.medicare },
                { l: "State Tax", w: sel.calc.without.stateTax, h: sel.calc.with125.stateTax, d: sel.calc.diff.stateTax },
                ...(config.stateData[state]?.sdi > 0 ? [{ l: "SDI/TDI", w: sel.calc.without.sdi, h: sel.calc.with125.sdi, d: sel.calc.diff.sdi }] : []),
                ...((config.stateData[state]?.pfml > 0 || config.stateData[state]?.fli > 0) ? [{ l: "PFML" + (config.stateData[state]?.fli > 0 ? "/FLI" : ""), w: sel.calc.without.pfml, h: sel.calc.with125.pfml, d: sel.calc.diff.pfml }] : []),
                { l: "Total Tax", w: sel.calc.without.totalTax, h: sel.calc.with125.totalTax, d: sel.calc.diff.totalTax, bold: true },
              ].map((r, i) => {
                if (r.divider) return <tr key={i}><td colSpan={4} style={{ padding: "10px 0 4px", fontSize: 10, fontWeight: 700, color: C.textLight, letterSpacing: "0.06em", textTransform: "uppercase", borderTop: `1px solid ${C.border}` }}>{r.divider}</td></tr>;
                return (
                  <tr key={i} style={{ background: r.result ? C.greenLight : "transparent" }}>
                    <td style={{ ...S.td, fontWeight: r.bold ? 700 : 400, color: r.bold ? C.text : C.textMid }}>{r.l}</td>
                    <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 12 }}>{fmt(r.w)}</td>
                    <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 12, color: r.hl ? C.accent : undefined, fontWeight: r.hl ? 700 : 400 }}>{fmt(r.h)}</td>
                    <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 12, fontWeight: r.result ? 800 : r.bold ? 700 : 400, color: r.result ? (r.d >= 0 ? C.green : C.red) : r.d > 0 ? C.green : undefined }}>{fmt(r.d)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Employer + Benefits Coverage */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div style={S.card}>
            <h3 style={S.heading}>Employer Savings — Zero Cost to Employer</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={{ background: C.blueLight, borderRadius: 10, padding: "14px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: C.textLight, marginBottom: 4, textTransform: "uppercase" }}>{frequencyLabel} FICA Savings</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: C.blue, ...S.mono }}>{fmt(sel.employerPerPeriodFica)}</div>
              </div>
              <div style={{ background: C.blueLight, borderRadius: 10, padding: "14px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: C.textLight, marginBottom: 4, textTransform: "uppercase" }}>Annual FICA Savings</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: C.blue, ...S.mono }}>{fmt(sel.employerAnnualFica)}</div>
              </div>
            </div>
            {config.engineSettings?.includeFutaSavings && sel.employerAnnualFuta > 0 && (
              <div style={{ marginTop: 10, padding: "8px 12px", background: C.blueLight, borderRadius: 8, fontSize: 11, color: C.blue }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>FUTA Savings ({frequencyLabel}):</span>
                  <span style={{ fontWeight: 700, ...S.mono }}>{fmt(sel.employerPerPeriodFuta)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>FUTA Savings (Annual):</span>
                  <span style={{ fontWeight: 700, ...S.mono }}>{fmt(sel.employerAnnualFuta)}</span>
                </div>
              </div>
            )}
            <div style={{ marginTop: 10, padding: "10px", background: C.greenLight, borderRadius: 8, textAlign: "center", fontSize: 12, color: C.green, fontWeight: 700, border: `1px solid ${C.greenBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Icon name="checkmark-circle" style={{ fontSize: '16px' }} />
              No out-of-pocket cost to employer — pure tax savings
            </div>
          </div>
          <div style={S.card}>
            <h3 style={S.heading}>Benefits Coverage — {sel.label}</h3>
            {benefits.length > 0 ? (
              <div style={{ fontSize: 12, color: C.textMid, lineHeight: 2 }}>
                {benefits.map((ben, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", borderBottom: i < benefits.length - 1 ? `1px solid ${C.borderLight}` : "none", padding: "2px 0" }}>
                    <span>{ben.name}</span>
                    <span style={{ fontWeight: 700, color: C.text, ...S.mono, fontSize: 12 }}>{fmtBenefitValue(ben)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: C.textLight, textAlign: 'center', padding: 16 }}>No benefits configured for this plan.</div>
            )}
          </div>
        </div>

        {/* Calculation Audit (Super Users Only) */}
        <CalculationAudit
          salary={salary}
          state={state}
          filing={filing}
          selectedPlan={sel}
          config={analysisConfig}
          currentUser={currentUser}
        />
      </div>
      </div>
    </div>
  );
}
